import Vector from '../../lib/Vector';
import { cleanUndefinedKeysRecurse, deg, modValIntoRange, rad } from '../../lib/util';
import SceneView from './SceneView';
import { sceneViewTypes } from './sceneViewTypes';

/**
 * @typedef {import('./SceneView').SceneViewData & ChildClassData} SceneViewPanoData
 * @combinedata
 */
/**
 * @typedef {Object} ChildClassData
 * @property {number} _scenePanoViewDataVer
 * @property {number} pitch=0
 * @property {number} yaw=0
 * @property {number} fov=75
 * @ignore
 */

const latestDataVer = 1;

/**
 * @extends SceneView
 */
export default class ScenePanoView extends SceneView {
	static _targetType = sceneViewTypes.pano;

	/**
	 * @param {SceneViewPanoData} inputData 
	 */
	constructor(inputData) {
		super(inputData);
	}

	/**
	 * @param {SceneViewPanoData} inputData 
	 */
	_reInit(inputData) {
		super._reInit(inputData);
		this._scenePanoViewDataVer = inputData._scenePanoViewDataVer || latestDataVer;

		if (this._scenePanoViewDataVer !== latestDataVer) { throw new Error(`Metadata version not latest: Got ${this._scenePanoViewDataVer}, should be ${latestDataVer}`); }

		this.pitch = parseFloat(inputData.pitch) || 0;
		this.yaw = parseFloat(inputData.yaw) || 0;
		this.fov = parseFloat(inputData.fov) || 75;
	}

	serialize() {
		const ser = {
			...super.serialize(),
			_scenePanoViewDataVer: this._scenePanoViewDataVer,
			pitch: +this.pitch.toFixed(5),
			yaw: +this.yaw.toFixed(5),
			fov: +this.fov.toFixed(5),
		};

		return cleanUndefinedKeysRecurse(ser);
	}

	getUpdatableData() {
		// we can only request to directly update these
		const updates = {
			...super.getUpdatableData(),
			_scenePanoViewDataVer: this._scenePanoViewDataVer, // even though we can't technically update this, send it so the backend can throw if we are sending the wrong data ver
			pitch: this.pitch,
			yaw: this.yaw,
			fov: this.fov,
		};

		return cleanUndefinedKeysRecurse(updates);
	}

	/**
	 * A helper function to allow easy creation of a new object of this class (abstracting away the data versioning and typing needed for the factory function)
	 * @returns {ScenePanoView}
	 */
	static #newFromInputs({ pitch, yaw, fov }) {
		return new ScenePanoView({
			pitch,
			yaw,
			fov,
		});
	}

	/**
	 * @param {Vector} vector 
	 */
	static fromVector(vector) {
		return this.#newFromInputs({ pitch: vector.y, yaw: vector.z });
	}

	static fromRad({ pitch, yaw, fov } = {}) {
		return this.#newFromInputs({
			pitch: deg(pitch),
			yaw: deg(yaw),
			fov: deg(fov),
		});
	}

	toRad() {
		return {
			pitch: rad(this.pitch),
			yaw: rad(this.yaw),
			fov: rad(this.fov),
		};
	}

	round({ decimals }) {
		return ScenePanoView.#newFromInputs({
			pitch: +this.pitch.toFixed(decimals),
			yaw: +this.yaw.toFixed(decimals),
			fov: +this.fov.toFixed(decimals),
		});
	}

	add(view) {
		return ScenePanoView.#newFromInputs({
			pitch: this.pitch + view.pitch,
			yaw: this.yaw + view.yaw,
			fov: this.fov + (view.fov || 0),
		});
	}

	subtract(view) {
		return ScenePanoView.#newFromInputs({
			pitch: this.pitch - view.pitch,
			yaw: this.yaw - view.yaw,
			fov: this.fov - (view.fov || 0),
		});
	}

	modIntoRange({ min = -180, max = 180 } = {}) {
		return ScenePanoView.#newFromInputs({
			pitch: modValIntoRange(this.pitch, min, max),
			yaw: modValIntoRange(this.yaw, min, max),
			fov: this.fov, // keep fov
		});
	}

	isDefault() { return super.isDefault() && (this.pitch === 0 && this.yaw === 0 && this.fov === 75); }
}